import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Link } from 'react-router';
import QuizIcon from '@mui/icons-material/Quiz';

interface PQCardProps {
  completedSections: number;
  totalSections: number;
  allComplete: boolean;
  pqPath: string;
  vaultSummaryPath: string;
  onContinueClick?: () => void;
}

export function PQCard({
  completedSections,
  totalSections,
  allComplete,
  pqPath,
  vaultSummaryPath,
  onContinueClick,
}: PQCardProps): React.JSX.Element {
  const theme = useTheme();

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '16px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        px: '16px',
      }}
    >
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pt: '16px', pb: '12px' }}
      >
        {/* Left group */}
        <Stack direction="row" alignItems="center" spacing="12px">
          {/* Icon circle */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <QuizIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          </Box>

          {/* Title block */}
          <Stack spacing="2px">
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              Pronto Questionnaire
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
              Information you can use in future certification files.
            </Typography>
          </Stack>
        </Stack>

        {/* Continue button — hidden when all sections complete */}
        {!allComplete && (
          <Box
            component={Link}
            to={pqPath}
            onClick={onContinueClick}
            sx={{
              bgcolor: 'primary.main',
              color: theme.palette.common.white,
              borderRadius: '8px',
              px: '12px',
              py: '6px',
              fontSize: 12,
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Continue
          </Box>
        )}
      </Stack>

      {/* Footer row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pt: '12px', pb: '16px' }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
          {completedSections} of {totalSections} sections complete
        </Typography>

        <Box
          component={Link}
          to={vaultSummaryPath}
          sx={{
            border: `1px solid ${theme.palette.text.secondary}`,
            borderRadius: '8px',
            px: '12px',
            py: '6px',
            fontSize: 12,
            color: 'text.primary',
            fontWeight: 'normal',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          See Vault Summary
        </Box>
      </Stack>
    </Box>
  );
}
